import { useEffect, useRef } from "react";

const useModalFocus = (modalKey, onClose) => {
  const dialogRef = useRef(null);
  const returnFocusRef = useRef(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!modalKey || !dialog) return undefined;
    const previousFocus = document.activeElement;
    if (previousFocus !== document.body && !previousFocus?.closest('[aria-modal="true"]')) {
      returnFocusRef.current = previousFocus;
    }
    if (document.activeElement === dialog) dialog.blur();
    dialog.focus();
    const background = [];
    // Hide and disable everything outside this dialog, including nested page siblings.
    for (let node = dialog; node.parentElement; node = node.parentElement) {
      for (const sibling of node.parentElement.children) {
        if (sibling === node) continue;
        background.push([sibling, sibling.inert, sibling.getAttribute("aria-hidden")]);
        sibling.inert = true;
        sibling.setAttribute("aria-hidden", "true");
      }
      if (node.parentElement === document.body) break;
    }
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        event.stopPropagation();
        closeRef.current();
      }
      if (event.key !== "Tab") return;
      const controls = Array.from(dialog.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex="0"]',
      )).filter(element => !element.closest('[hidden], [inert]'));
      const first = controls[0];
      const last = controls[controls.length - 1];
      if (!first) {
        event.preventDefault();
        dialog.focus();
      } else if (event.shiftKey && (document.activeElement === first || document.activeElement === dialog)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (document.activeElement === last || document.activeElement === dialog)) {
        event.preventDefault();
        first.focus();
      }
    };
    dialog.addEventListener("keydown", handleKeyDown);
    return () => {
      dialog.removeEventListener("keydown", handleKeyDown);
      background.forEach(([element, inert, hidden]) => {
        element.inert = inert;
        if (hidden === null) element.removeAttribute("aria-hidden");
        else element.setAttribute("aria-hidden", hidden);
      });
      if (returnFocusRef.current?.isConnected) returnFocusRef.current.focus();
    };
  }, [modalKey]);

  return dialogRef;
};

export default useModalFocus;
